import {
  Drawer,
  DrawerContent,
  DrawerFooter,
} from '@finn/design-system/atoms/drawer';
import { useMediaQuery } from '@finn/design-system/helpers/media';
import { useDownPayment } from '@finn/ua-featureflags';
import {
  interactionTrackingEvent,
  TrackingEventName,
  useTrackingStore,
} from '@finn/ua-tracking';
import { cn, getFormattedPrice, getTermLabel, useIntl } from '@finn/ui-utils';
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { getForBusinessCookie } from '../../../helpers';
import { useDiscoveryConfig } from '../../core/data/MetadataProvider';
import { VehicleHeader } from '../../pdp';
import { useConfigureStore } from '../../pdp-management';
import {
  PartialCartInfo,
  useMonthlySubscriptionCost,
  usePartnerDiscount,
  usePreDiscountRawValue,
} from '../../product-pricing';
import { PCPStep, stepNameMap, useWizardSteps } from '../hooks/useWizardSteps';
import { ContinueButton } from './ContinueButton';
import { ContractDuration } from './ContractDuration';
import { DistanceOptions } from './DistanceOptions';
import { DownPayment } from './DownPayment';
import { PriceDisplay } from './PriceDisplay';

type ConfigureVehicleModalProps = {
  open: boolean;
  onClose: () => void;
  loadingEdit?: boolean;
  handleEditConfig?: () => void;
  closedUserGroupId?: string;
  cartInfo?: PartialCartInfo;
  vehicleHeaderChildren?: ReactNode;
  extraPCPSteps?: {
    id: string;
    renderStep: ({
      prevStep,
      currentStep,
    }: {
      prevStep: PCPStep;
      currentStep: PCPStep;
    }) => ReactNode;
  }[];
};

const ANIMATION_TIMEOUT = 300;

export const ConfigureVehicleModal = ({
  open,
  onClose,
  handleEditConfig,
  loadingEdit,
  closedUserGroupId,
  cartInfo,
  vehicleHeaderChildren,
  extraPCPSteps = [],
}: ConfigureVehicleModalProps) => {
  const { isDownPaymentEnabled } = useDownPayment();
  const intl = useIntl();
  const partnerDiscount = usePartnerDiscount({
    closedUserGroupId,
    cartInfo,
  });
  const track = useTrackingStore((state) => state.track);

  useEffect(() => {
    if (partnerDiscount) {
      track(TrackingEventName.PCP_PARTNER_DISCOUNT_SHOWED, {
        additionalProps: { partnerDiscount },
      });
    }
  }, [partnerDiscount, track]);

  const {
    vehicle,
    term,
    onContinue,
    downPayment: isDownPayment,
    isLoading: isLoadingFromStore,
    kilometerPackage,
  } = useConfigureStore();
  const [continueDisabled, setContinueDisabled] = useState(false);
  const { forceRegularPrice } = useDiscoveryConfig();
  const [submitting, setSubmitting] = useState(false);
  const hasOnlyOneTerm = vehicle?.available_terms.length === 1;

  const onWizardFinish = useCallback(async () => {
    try {
      setSubmitting(true);
      await onContinue();
    } catch (error) {
      // ignore
    } finally {
      setSubmitting(false);
    }
  }, [onContinue]);

  const { currentStep, prevStep, goNext, goBack, isFirstStep, progress } =
    useWizardSteps({
      hasOnlyOneTerm,
      isDownPaymentEnabled,
      onFinish: onWizardFinish,
    });

  const scrollContainer = useRef<HTMLDivElement>(null);

  const isForBusiness = getForBusinessCookie();

  const { total, monthlyCost } = useMonthlySubscriptionCost(vehicle);
  let totalWithDiscount = total;
  let preDiscountPrice = usePreDiscountRawValue({
    vehicle,
    price: monthlyCost,
    term,
    km: kilometerPackage,
    isForBusiness,
    isDownPayment,
  });

  if (partnerDiscount > 0) {
    if (!preDiscountPrice) {
      preDiscountPrice = total;
    }
    totalWithDiscount = total - total * partnerDiscount;
  }

  const originalPriceFormatted = getFormattedPrice(
    intl,
    preDiscountPrice || 0,
    0
  );
  const finalPriceFormatted = getFormattedPrice(intl, totalWithDiscount, 0);

  const subtitle = intl.formatMessage(
    `pdp.price.${isForBusiness || forceRegularPrice ? 'b2b' : 'b2c'}_vat`
  );

  const isShortPreDiscountPrice = preDiscountPrice?.toString().length === 4;

  const handleModalClose = useCallback(() => {
    interactionTrackingEvent(TrackingEventName.MODAL_CLOSED, {
      name: 'configure',
      location: stepNameMap[currentStep],
    });
    onClose();
  }, [onClose, currentStep]);

  const tempDisableContinue = useCallback(() => {
    setContinueDisabled(true);
    setTimeout(() => {
      setContinueDisabled(false);
    }, ANIMATION_TIMEOUT);
  }, []);

  const handleContinue = useCallback(async () => {
    scrollContainer.current?.scrollTo(0, 0);
    if (handleEditConfig) {
      handleEditConfig();

      return;
    }

    goNext();
    tempDisableContinue();
  }, [goNext, handleEditConfig, tempDisableContinue, currentStep]);

  const isMobile = useMediaQuery('md');
  const i18n = useIntl();

  const buildConfigureSection = useMemo(() => {
    if (hasOnlyOneTerm) return null;

    return (
      <>
        <VehicleHeader vehicle={vehicle}>{vehicleHeaderChildren}</VehicleHeader>

        <ContractDuration />
      </>
    );
  }, [vehicle, vehicleHeaderChildren, hasOnlyOneTerm]);

  const buildDownPaymentSection = useMemo(() => {
    return (
      <>
        <VehicleHeader vehicle={vehicle}>{vehicleHeaderChildren}</VehicleHeader>
        <DownPayment />
      </>
    );
  }, [vehicle, vehicleHeaderChildren]);

  const buildFooterSection = useMemo(() => {
    const location = stepNameMap[currentStep];
    const continueToCart = i18n.formatMessage('deductibles.continueToCart');

    return (
      <DrawerFooter className="z-overlay">
        <PriceDisplay
          subtitle={subtitle}
          finalPrice={finalPriceFormatted}
          originalPrice={originalPriceFormatted}
          isShortPrice={isShortPreDiscountPrice}
          hasOriginalPrice={Boolean(preDiscountPrice)}
        />

        <div className="w-1/2">
          <ContinueButton
            isInPDP
            testId="submit-config"
            title={location === 'insurance' ? continueToCart : undefined}
            location={location}
            onClick={handleContinue}
            isLoading={
              isLoadingFromStore ||
              loadingEdit ||
              continueDisabled ||
              submitting
            }
          />
        </div>
      </DrawerFooter>
    );
  }, [
    submitting,
    currentStep,
    subtitle,
    finalPriceFormatted,
    originalPriceFormatted,
    isShortPreDiscountPrice,
    preDiscountPrice,
    handleContinue,
    isLoadingFromStore,
    loadingEdit,
    continueDisabled,
    i18n,
  ]);

  const backButton = useMemo(() => {
    if (isFirstStep) {
      return;
    }

    return () => {
      interactionTrackingEvent(TrackingEventName.BACK_BUTTON_CLICKED, {
        location: stepNameMap[currentStep],
      });
      goBack();
    };
  }, [currentStep, goBack, isFirstStep]);

  const flexTermLabel = i18n.formatMessage('checkout.pageData.flex_term_label');

  return (
    <Drawer
      open={open}
      onClose={handleModalClose}
      handleOnly
      direction={isMobile ? 'bottom' : 'right'}
    >
      <DrawerContent
        title={intl.formatMessage('pdp.configure.generalTitle')}
        onBack={backButton}
        // TODO: test that nothing broke here
        className="z-modalOverlay min-h-dvh !select-text overflow-y-hidden [&>div]:h-full"
      >
        <div
          ref={scrollContainer}
          className={cn(
            'absolute left-0 top-14 size-full grow overflow-y-auto overflow-x-hidden border-0 border-t border-solid border-pearl md:top-[72px]',
            'max-h-[calc(100%-10rem)] md:max-h-[calc(100%-11rem)]'
          )}
        >
          <div className="absolute left-0 top-0 z-modalOverlay h-[4px] w-full">
            <div
              style={{
                width: `${progress}%`,
              }}
              className={cn(
                'h-[4px] bg-trustedBlue transition-all duration-300'
              )}
            />
          </div>
          {!hasOnlyOneTerm && (
            <div
              id="configure-step"
              className={cn(
                'absolute left-0 top-0 flex size-full flex-col gap-8 bg-white px-4 py-6 transition-[transform,opacity] md:px-20',
                currentStep === PCPStep.ConfigureStep
                  ? 'z-overlay translate-x-0 opacity-100'
                  : `-top-full z-base -translate-x-28 opacity-0`
              )}
            >
              {buildConfigureSection}
            </div>
          )}
          <div
            id="distance-step"
            className={cn(
              'absolute left-0 top-0 flex size-full flex-col gap-8 bg-white px-4 py-6 transition-[transform,opacity] md:px-20',
              currentStep === PCPStep.DistanceStep
                ? 'z-overlay translate-x-0 opacity-100'
                : `-top-full z-base ${prevStep && prevStep > currentStep ? '-translate-x-28' : 'translate-x-28'} opacity-0`
            )}
          >
            <VehicleHeader vehicle={vehicle} />

            {hasOnlyOneTerm && (
              <div>
                <h6 className="mb-4 body-16-semibold">
                  {i18n.formatMessage('pdp.contractTerm')}
                </h6>
                <div className="body-14-regular">
                  {getTermLabel(
                    vehicle.available_terms[0],
                    flexTermLabel,
                    i18n
                  )}
                </div>
              </div>
            )}
            <DistanceOptions />
          </div>
          <div
            id="downpayment-step"
            className={cn(
              'absolute left-0 top-0 flex size-full flex-col gap-8 bg-white px-4 py-6 transition-[transform,opacity] md:px-20',
              currentStep === PCPStep.DownPaymentStep
                ? 'z-overlay translate-x-0 opacity-100'
                : `-top-full z-base ${prevStep && prevStep > currentStep ? '-translate-x-28' : 'translate-x-28'} opacity-0`
            )}
          >
            {buildDownPaymentSection}
          </div>
          {/* Extra Steps - For now, it's only the insurance step for B2B App. */}
          {extraPCPSteps?.map((step) =>
            prevStep
              ? step.renderStep({
                  prevStep,
                  currentStep,
                })
              : null
          )}
        </div>
        {buildFooterSection}
      </DrawerContent>
    </Drawer>
  );
};
